/**
 * FunnelReport — how far leads GOT, as CSS bars, with where they are sitting
 * right now shown beside it.
 *
 * The bars count EVER REACHED (owner, 2026-08-18: "if a patient ever reached
 * that status the count needs to account for that lead being in that status
 * even if they progress to the next one"). Until then these were point-in-time
 * occupancy counts, which meant a lead that screened and then randomized
 * SUBTRACTED itself from Screened on the way past — the middle of the funnel
 * drained as recruitment succeeded, which is the opposite of what a funnel is
 * for. crm.funnel_stats now returns both numbers per stage and this component
 * leads with the cumulative one.
 *
 * Occupancy is not thrown away, because "how many are sitting in scheduled
 * right now" is the question the Kanban board answers and a recruiter still
 * wants it here: it rides along as the "N here now" note under each stage.
 *
 * The forward spine is cumulative and therefore MONOTONIC — each stage is a
 * subset of the one above it — so the bar widths finally describe a funnel and
 * the % is an honest share of the cohort. The exit lane below the divider
 * counts leads that ever landed in that exit, which is not a subset of anything
 * and is scaled against the cohort rather than against the spine.
 */
import { useEffect, useMemo, useState } from 'react';
import api from '../../../services/api';
import { FunnelRow } from '../../types';
import { LIFECYCLES } from '../../lib';
import BarRow from './BarRow';
import {
  buildGroups, countsAt, reachedShare, spineMax, EXIT_ORDER, FUNNEL_ORDER, GroupFunnel,
} from './funnelLogic';

interface FunnelReportProps {
  groupBy: 'overall' | 'study' | 'lead_source' | 'recruiter';
  from: string | null;
  to: string | null;
  /** When set, only this group's funnel renders (use with groupBy='study'). */
  onlyGroupKey?: string | null;
  /** Scope to one study via the contact's current primary study. */
  studyId?: string | null;
}

const STAGE_COLORS: Record<string, string> = {
  new: 'bg-cyan-500', contacting: 'bg-blue-500', qualified_pending_records: 'bg-indigo-500',
  ready_to_schedule: 'bg-teal-500', scheduled: 'bg-amber-500', screened: 'bg-purple-500',
  randomized: 'bg-green-500', screen_failed: 'bg-rose-400', not_eligible: 'bg-red-400',
  not_interested: 'bg-gray-400', unreachable: 'bg-orange-400',
};

export default function FunnelReport({ groupBy, from, to, onlyGroupKey = null, studyId = null }: FunnelReportProps) {
  const [rows, setRows] = useState<FunnelRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get('/api/crm/analytics/funnel', { params: { group_by: groupBy, from: from || undefined, to: to || undefined, study_id: studyId || undefined } })
      .then((res) => { if (alive) { setRows(res.data?.rows || []); setError(null); } })
      .catch((err) => { if (alive) setError(err?.response?.data?.error || 'Failed to load funnel'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [groupBy, from, to, studyId]);

  const groups: GroupFunnel[] = useMemo(
    () => buildGroups(rows, onlyGroupKey),
    [rows, onlyGroupKey],
  );

  if (loading) return <p className="p-4 text-sm text-gray-400">Loading funnel…</p>;
  if (error) return <p className="p-4 text-sm text-red-600 dark:text-red-400">{error}</p>;
  if (groups.length === 0) return <p className="p-4 text-sm text-gray-500 dark:text-gray-400">No contacts in this range yet.</p>;

  // The classic funnel reads top-down through the considering stages (FUNNEL_ORDER);
  // terminal exits render below the divider. Both orders, and the arithmetic
  // under them, live in funnelLogic so they can be tested without a DOM.
  const renderFunnel = (g: GroupFunnel) => {
    const at = (s: string) => countsAt(g, s);
    const max = spineMax(g);
    return (
      <div key={g.key} className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
        <div className="mb-2 flex items-center justify-between gap-2">
          <h3 className="min-w-0 truncate text-sm font-semibold text-gray-900 dark:text-gray-100">{g.label}</h3>
          <span className="flex-shrink-0 text-xs text-gray-400">{g.total.toLocaleString()} leads</span>
        </div>
        <div className="space-y-0.5">
          {FUNNEL_ORDER.map((status) => {
            const { reached, here } = at(status);
            const meta = LIFECYCLES.find((l) => l.value === status);
            const hint = reachedShare(g, status);
            return (
              <div key={status}>
                <BarRow
                  label={meta?.label || status}
                  count={reached}
                  max={max}
                  hint={hint}
                  color={STAGE_COLORS[status] || 'bg-gray-400'}
                />
                {/* Only where it says something the bar does not: a stage nobody
                    has moved past yet would just repeat the number. */}
                {here > 0 && here !== reached && (
                  <p className="px-2 pb-1 text-xs text-gray-400 dark:text-gray-500">
                    {here.toLocaleString()} here now
                  </p>
                )}
              </div>
            );
          })}
        </div>
        <div className="mt-2 space-y-0.5 border-t border-gray-100 pt-2 dark:border-gray-700">
          {EXIT_ORDER.map((status) => {
            const { reached, here } = at(status);
            if (!reached && !here) return null;
            const meta = LIFECYCLES.find((l) => l.value === status);
            return (
              <div key={status}>
                <BarRow
                  label={meta?.label || status}
                  count={reached}
                  // Exits are not a subset of the spine, so they scale against
                  // the cohort instead — a bar as wide as the funnel's top would
                  // otherwise claim everyone exited here.
                  max={Math.max(g.total, 1)}
                  color={STAGE_COLORS[status] || 'bg-gray-400'}
                />
                {here > 0 && here !== reached && (
                  <p className="px-2 pb-1 text-xs text-gray-400 dark:text-gray-500">
                    {here.toLocaleString()} here now
                  </p>
                )}
              </div>
            );
          })}
        </div>
        <p className="mt-3 border-t border-gray-100 pt-2 text-xs text-gray-400 dark:border-gray-700 dark:text-gray-500">
          Bars count everyone who <span className="font-medium">ever reached</span> a stage, so a lead that moved on
          still counts in the stages behind it. “Here now” is who is sitting there today.
        </p>
      </div>
    );
  };

  if (groupBy === 'overall' || onlyGroupKey) {
    return <div className="max-w-xl">{groups.map(renderFunnel)}</div>;
  }
  return <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">{groups.map(renderFunnel)}</div>;
}
